import { useEffect, useId, useRef, useState } from 'react';
import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import { Alert, Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';

import { screenshotError } from '../screenshot';
import type { SupportDraft } from '../types';

export function SupportComposer({
  draft,
  onChange,
  replyEmail,
  locked,
  onSubmit,
  onAttachmentSelected,
}: {
  draft: SupportDraft;
  onChange: (draft: SupportDraft) => void;
  replyEmail: string;
  locked: boolean;
  onSubmit: () => void;
  onAttachmentSelected?: () => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const messageInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const input = messageInput.current;
    // Focus a newly opened/restored form only while the support panel owns focus.
    // A response arriving while someone uses the workspace must not steal it.
    if (input?.closest('[role="dialog"]')?.contains(document.activeElement)) {
      input.focus({ preventScroll: true });
    }
  }, []);
  const hintId = useId();
  const [fileError, setFileError] = useState<string | null>(null);
  return (
    <Box
      component="form"
      onSubmit={event => {
        event.preventDefault();
        if (!locked) onSubmit();
      }}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            alignSelf: 'flex-start',
            bgcolor: 'action.hover',
            p: 1.5,
            borderRadius: '16px 16px 16px 4px',
          }}
        >
          <Typography variant="body2">Hi! What can we help with?</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          Our support team will reply to {replyEmail} by email.
        </Typography>
        <TextField
          inputRef={messageInput}
          placeholder="Write a message…"
          value={draft.message}
          onChange={event => onChange({ ...draft, message: event.target.value })}
          disabled={locked}
          required
          multiline
          minRows={3}
          maxRows={7}
          fullWidth
          slotProps={{ htmlInput: { maxLength: 8000, 'aria-label': 'How can we help?' } }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
        />
        <Box>
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            aria-label="Choose screenshot"
            hidden
            disabled={locked}
            onChange={event => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (!file) return;
              const error = screenshotError(file);
              setFileError(error);
              if (!error) {
                onChange({ ...draft, screenshot: file });
                onAttachmentSelected?.();
              }
            }}
          />
          {draft.screenshot ? (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                pl: 1.5,
              }}
            >
              <AttachFileRounded fontSize="small" color="action" />
              <Typography variant="body2" sx={{ flex: 1, overflowWrap: 'anywhere' }}>
                {draft.screenshot.name}
              </Typography>
              <IconButton
                aria-label="Remove screenshot"
                disabled={locked}
                onClick={() => {
                  onChange({ ...draft, screenshot: null });
                  setFileError(null);
                }}
              >
                <CloseRounded fontSize="small" />
              </IconButton>
            </Stack>
          ) : (
            <Button
              variant="text"
              size="small"
              startIcon={<AttachFileRounded />}
              disabled={locked}
              onClick={() => fileInput.current?.click()}
              aria-describedby={hintId}
            >
              Attach a screenshot
            </Button>
          )}
          <Typography
            id={hintId}
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: 'block' }}
          >
            Optional · PNG, JPEG, or WebP · up to 3 MB
          </Typography>
          {fileError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {fileError}
            </Alert>
          )}
        </Box>
        <Button
          type="submit"
          variant="contained"
          endIcon={<SendRounded />}
          sx={{ alignSelf: 'flex-end', borderRadius: 5, px: 2.5 }}
          disabled={locked || !draft.message.trim()}
        >
          Send message
        </Button>
      </Stack>
    </Box>
  );
}
